<template>
    <div>

        <v-alert
            color="secondary"
            text
            class="file-drop pa-0 ma-0"
        >
            <slot></slot>
            <v-progress-circular v-if="loading" size="24" class="ma-2" indeterminate color="primary" />
            <v-file-input
                v-else-if="imgUrl === undefined && !localValue"
                v-model="file"
                flat
                block
                prepend-icon="mdi-image-plus"
                hide-details
                style="border:0px;"
                label="Bild hochladen"
                @change="loadImage"
                accept="image/*"
                type="file"
                :rules="rules"
                :disabled="disabled"
            >
            </v-file-input>
            <div v-else class="d-flex flex-column align-center pa-2 img-container">
                <div v-if="localValue === undefined" class="d-flex flex-column ga-2 align-center">
                    <span>
                        <v-icon color="secondary" class="mr-1 mb-1">mdi-account-box-outline</v-icon>
                        <strong>Wähle bitte einen passenden Ausschnitt.</strong>
                    </span>
                    <popup-dialog-base
                        title="Passfoto-Ausschnitt"
                        subtitle="Wähle bitte einen passenden Ausschnitt."
                        icon="mdi-account-box-outline"
                        ref="CropImage"
                        :toolButtons="buttons"
                        :onClose="deleteImage"
                    >
                        <template v-slot:content>
                            <div class="pa-1">
                                <Cropper
                                    ref="cropper"
                                    :src="imgUrl"
                                    :quality="quality"
                                    :aspect-ratio="resizeWidth / resizeHeight"
                                    :resizeWidth="resizeWidth"
                                    :resizeHeight="resizeHeight"
                                />
                            </div>
                        </template>
                    </popup-dialog-base>
                </div>
                <div v-else class="d-flex">
                    <img
                        :src="localValue"
                        class="elevation-2"
                        style="position:relative; z-index:0;"
                        :style="{
                            'max-width':Math.min(resizeWidth, maxDisplayWidth)+'px', 
                            'max-height':Math.min(resizeHeight, maxDisplayHeight)+'px',
                            'border-radius':radius+'px'
                        }"
                    >
                    <div
                        class="d-flex flex-column"
                        style="position:absolute; right:10px; top:10px; z-index:1;"
                    >
    
                        <v-btn 
                            icon
                            color="secondary"
                            @click="deleteImage"
                            :disabled="disabled"
                        >
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                        <v-btn 
                            icon
                            color="secondary"
                            @click="downloadImage"
                        >
                            <v-icon>mdi-download</v-icon>
                        </v-btn>
                    </div>
                </div>
            </div>
        </v-alert>
    </div>
</template>

<script>
import Cropper from '../Cropper.vue';
import PopupDialogBase from '../PopupDialogBase.vue';
export default {
    components:{
        Cropper,
        PopupDialogBase
    },
    props:{
        value:{type:String},
        quality:{type:Number, default:0.7},
        radius:{type:Number, default:10},
        resizeWidth:{type:Number, default:256},
        resizeHeight:{type:Number, default:384},
        maxDisplayWidth:{type:Number, default:256},
        maxDisplayHeight:{type:Number, default:256},
        name:{type:String, default:"avatar"},
        rules: {
            type: Array,
            required: false
        },
        disabled:{type:Boolean, default:false}
    },
    data(){
        return{
            file:undefined,
            localValue:undefined,
            imgUrl:undefined,
            loading:false,
            fileType:'image/jpeg',
            size:undefined,
            buttons:[
                {label:"Bestätigen", color:"primary", icon:"mdi-check", method:this.confirmCrop, viewCondition:()=>{return true}},
            ],
        }
    },
    created(){
        this.localValue = this.value;
    },
    watch:{
        value(value){
            this.localValue = value;
        }
    },
    methods:{
        changeValue(value){
            this.$emit("input", value);
        },
        async loadImage(file){
            this.localValue = undefined;
            this.file = file;
            this.loading = true;
            this.imgUrl = await this.convertToPNGBase64(file);
            this.loading = false;

            this.$nextTick(()=>{
                this.$refs["CropImage"].show()
            })
        },
        async confirmCrop(){
            const url = this.$refs.cropper.getCroppedImage()
            this.localValue = url;
            this.size = this.base64FileSize(url);
            this.changeValue(url);
        },
        base64FileSize(base64String) {
            // Remove metadata if present
            const base64 = base64String.split(',')[1] || base64String;
            const padding = (base64.endsWith('==')) ? 2 : (base64.endsWith('=')) ? 1 : 0;
            const sizeInBytes = (base64.length * 3 / 4) - padding;
            
            // Optional conversions
            const sizeInKB = sizeInBytes / 1024;
            const sizeInMB = sizeInKB / 1024;

            return { bytes: sizeInBytes, kb: sizeInKB, mb: sizeInMB };
        },
        base64ToFile(base64, filename) {
            const arr = base64.split(',');
            const mime = arr[0].match(/:(.*?);/)[1];
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);

            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }

            return new File([u8arr], filename, { type: mime });
        },
        deleteImage(){
            this.localValue = undefined;
            this.imgUrl = undefined;
            this.file = undefined;
            this.size = undefined;
            this.changeValue("");
        },
        downloadImage(){
            const link = document.createElement('a');
            link.href = this.localValue;
            link.download = this.name+'.jpg';
            link.click();
        },
        convertToPNGBase64(blob, maxWidth=512, maxHeight=512){
            return new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = (e) => {
                    const img = new Image();
                    img.onload = () => {
                        const canvas = document.createElement('canvas');

                        let width = img.naturalWidth;
                        let height = img.naturalHeight;

                        // 1. Calculate the new dimensions while maintaining aspect ratio
                        if (width > maxWidth) {
                            height = height * (maxWidth / width);
                            width = maxWidth;
                        }

                        if (height > maxHeight) {
                            width = width * (maxHeight / height);
                            height = maxHeight;
                        }

                        canvas.width = width;
                        canvas.height = height;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0, width, height);
                        
                        const pngUrl = canvas.toDataURL('image/png');
                        resolve(pngUrl);
                    }
                    img.onerror = reject;
                    img.src = e.target.result;
                }

                reader.onerror = reject;
                reader.readAsDataURL(blob)
            })
        },
    }
}
</script>

<style scoped>
.ga-2{
    gap: 0.5rem;
}
.img-container{
    gap:1rem;
}</style>